import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Users, Flame, TrendingUp, CalendarDays, AlertTriangle, Clock, ArrowRight, CheckCircle2 } from 'lucide-react';
import { PageLayout } from '../../shared/PageLayout';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { DjayNarrator } from '../../../mascot';
import { MODULES } from '../../../content';
import { hasActiveDecay, getDecayStatus, type FamilyMasteryState } from '../../../mastery';
import { getWeeklyActivityForLearner } from '../../../utils/activityHelpers';
import { type LearnerAccount } from '../../../accounts';
import { AppState } from '../../../app/types';
import { ParentV2Route } from '../ParentSidebarV2';
import { DataFreshness } from '../../shared/DataFreshness';
import { MS_PER_DAY } from '../../../config/constants';
import { getChildLastActivity } from '../shared/childActivity';
interface ComparePageProps {
  linkedLearners: LearnerAccount[];
  selectedChild: LearnerAccount | undefined;
  getLearnerData: (learnerId: string) => AppState | null;
  getLearnerModuleMastery: (learnerId: string, moduleId: string) => number;
  setViewingLearner: (id: string | null) => void;
  onNavigate?: (route: ParentV2Route) => void;
}
interface ChildSnapshot {
  learner: LearnerAccount;
  data: AppState | null;
  overallMastery: number;
  currentStreak: number;
  daysThisWeek: number;
  lastActivityTs: number | null;
  staleDecayCount: number;
  fadingDecayCount: number;
  attentionScore: number;
  attentionReason: string | null;
}
function relativeTimeNL(ts: number | null): string {
  if (!ts) return 'Nog niet geoefend';
  const now = Date.now();
  const diffMs = now - ts;
  const diffDays = Math.floor(diffMs / MS_PER_DAY);
  if (diffDays === 0) return 'Vandaag';
  if (diffDays === 1) return 'Gisteren';
  if (diffDays < 7) return `${diffDays} dagen geleden`;
  if (diffDays < 14) return '1 week geleden';
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weken geleden`;
  return `${Math.floor(diffDays / 30)} maanden geleden`;
}
/**
 * ComparePage — Multi-child side-by-side overzicht (W4-1, audit F-RUN-7).
 *
 * Persona-doel: Multi-child Mara — "Wie heeft mijn aandacht nu nodig?"
 *
 * Toont per gekoppeld kind een compacte snapshot-kaart met:
 *  - Overall mastery, current streak, dagen deze week, laatst geoefend
 *  - Een "Heeft aandacht nodig" banner bij stale-decay of >=3 dagen inactief
 *  - Een directe drill-in via "Bekijk details" (zet viewingLearner + nav home)
 *
 * Sortering: kinderen met de hoogste attentionScore staan bovenaan zodat
 * Mara in één blik ziet welk kind nu de bottleneck is. Bij gelijke score
 * blijft de stabiele linkedLearners-volgorde behouden.
 *
 * Beperkingen: read-only, alleen zichtbaar bij >=2 kinderen — voor 1 kind
 * verwijst de page door naar home (single-child heeft geen vergelijk-job).
 */
export function ComparePage({
  linkedLearners,
  getLearnerData,
  getLearnerModuleMastery,
  setViewingLearner,
  onNavigate
}: ComparePageProps) {
  const snapshots = useMemo<ChildSnapshot[]>(() => {
    const now = Date.now();
    return linkedLearners.map(learner => {
      const data = getLearnerData(learner.id);
      // Overall mastery — average across modules with mastery > 0 (matches Vakken hero)
      let masterySum = 0;
      let masteryCount = 0;
      for (const module of MODULES) {
        const m = getLearnerModuleMastery(learner.id, module.id);
        if (m > 0) {
          masterySum += m;
          masteryCount++;
        }
      }
      const overallMastery = masteryCount > 0 ? Math.round(masterySum / masteryCount) : 0;
      // Weekly activity
      const weekly = data ? getWeeklyActivityForLearner(data.questionStates || {}) : [];
      const daysThisWeek = weekly.filter(d => d.practiced).length;
      // Current streak — prefer stats.streakState.currentStreak when present
      const currentStreak = data?.stats?.streakState?.currentStreak || data?.dailyStreak?.currentWeekDays || 0;
      // Last activity timestamp — shared helper, single source of truth
      // for parent surfaces (D-P2).
      const lastActivityTs = getChildLastActivity(data);
      // Decay counts (stale & fading)
      let staleDecayCount = 0;
      let fadingDecayCount = 0;
      if (data?.familyMastery) {
        for (const state of Object.values(data.familyMastery)) {
          const fs = state as FamilyMasteryState;
          if (!hasActiveDecay(fs, now)) continue;
          const status = getDecayStatus(fs, now);
          if (status === 'stale') staleDecayCount++;else if (status === 'fading') fadingDecayCount++;
        }
      }
      // Attention score — higher = more urgent
      // - Stale decay weighs heavily (knowledge actively fading)
      // - No practice in >=3 days weighs
      // - Mastery has practice history but currently 0 days this week weighs less
      let attentionScore = 0;
      let attentionReason: string | null = null;
      const daysSinceLast = lastActivityTs ? Math.floor((now - lastActivityTs) / MS_PER_DAY) : 999;
      if (staleDecayCount >= 1) {
        attentionScore += 100 + staleDecayCount * 10;
        attentionReason = `${staleDecayCount} ${staleDecayCount === 1 ? 'vak' : 'vakken'} zakt weg`;
      }
      if (daysSinceLast >= 3 && masteryCount > 0) {
        attentionScore += 50 + Math.min(daysSinceLast, 30) * 2;
        if (!attentionReason) {
          attentionReason = `${daysSinceLast} dagen niet geoefend`;
        }
      }
      if (daysThisWeek === 0 && masteryCount > 0 && !attentionReason) {
        attentionScore += 20;
        attentionReason = 'Deze week nog niet geoefend';
      }
      return {
        learner,
        data,
        overallMastery,
        currentStreak,
        daysThisWeek,
        lastActivityTs,
        staleDecayCount,
        fadingDecayCount,
        attentionScore,
        attentionReason
      };
    });
  }, [linkedLearners, getLearnerData, getLearnerModuleMastery]);
  // Sort: highest attention first, stable for equal scores
  const ranked = useMemo(() => {
    return [...snapshots].map((s, idx) => ({
      snapshot: s,
      idx
    })).sort((a, b) => {
      if (b.snapshot.attentionScore !== a.snapshot.attentionScore) {
        return b.snapshot.attentionScore - a.snapshot.attentionScore;
      }
      return a.idx - b.idx;
    }).map(x => x.snapshot);
  }, [snapshots]);
  const childrenNeedingAttention = ranked.filter(s => s.attentionScore > 0).length;
  // Latest activity across all children — for DataFreshness.
  // Returns null when no child has any recorded activity (D-P2): better to
  // show "Nog geen activiteit" than a falsy Date.now() placeholder.
  const latestActivityAcross = useMemo<number | null>(() => {
    let max: number | null = null;
    for (const s of snapshots) {
      if (s.lastActivityTs && (max === null || s.lastActivityTs > max)) {
        max = s.lastActivityTs;
      }
    }
    return max;
  }, [snapshots]);
  // Single-child redirect — compare has no job here
  if (linkedLearners.length < 2) {
    return <PageLayout title="Vergelijken" description="Vergelijk je kinderen naast elkaar" data-id="element-8070" data-mp="1vtbhjak0u4ou">
        <div className="max-w-2xl mx-auto py-16" data-id="element-8071" data-mp="1mqk3r43oawu1">
          <Card className="p-10 bg-white border-slate-200 shadow-sm flex flex-col items-center text-center gap-5" data-id="element-8072" data-mp="1mrv6d0ldtyjk">
            <DjayNarrator size="md" emotion="thinking" data-id="element-8073" data-mp="2jfrucqj5fw4t" />
            <div data-id="element-8074" data-mp="2feppnu6gqrvg">
              <h2 className="text-lg font-black text-slate-900 mb-2" data-id="element-8075" data-mp="2fg955ikaoyw6">
                Nog geen vergelijking mogelijk
              </h2>
              <p className="text-sm text-slate-600 max-w-md mx-auto" data-id="element-8076" data-mp="2alwm39mqdpky">
                Koppel een tweede kind in Instellingen om hier een naast-elkaar
                vergelijking te zien. Voor één kind zie je alle details op
                Overzicht en Vakken.
              </p>
            </div>
            <div className="flex gap-3" data-id="element-8077" data-mp="2ybzanfrfsdet">
              <Button variant="secondary" onClick={() => onNavigate?.('home')} data-id="element-8078" data-mp="26pz6lnqvspie">
                Naar Overzicht
              </Button>
              <Button onClick={() => onNavigate?.('settings')} data-id="element-8079" data-mp="2bia82v19sjd2">
                Naar Instellingen
              </Button>
            </div>
          </Card>
        </div>
      </PageLayout>;
  }
  return <PageLayout title="Vergelijken" description={`Naast elkaar overzicht van je ${linkedLearners.length} kinderen`} actions={<DataFreshness updatedAt={latestActivityAcross} data-id="element-8081" data-mp="2ufl508zfj0pc" />} data-id="element-8080" data-mp="21rfjvl4gr9oz">
      <div className="max-w-7xl mx-auto space-y-8 pb-12" data-id="element-8082" data-mp="2uf1wtjzkzqoi">
        {/* Hero — sets the JTBD framing for Mara */}
        <Card className="p-6 sm:p-8 bg-emerald-600 border-emerald-700 shadow-sm" data-id="element-8083" data-mp="2giepqodwzk4v">
          <div className="flex items-start gap-5" data-id="element-8084" data-mp="2kfud8f296zsy">
            <div className="hidden sm:flex w-20 h-20 bg-white/15 rounded-2xl items-center justify-center flex-shrink-0" data-id="element-8085" data-mp="150dl7kytecrj">
              <DjayNarrator size="md" emotion="thinking" data-id="element-8086" data-mp="193a0b1p5bqqq" />
            </div>
            <div className="flex-1 min-w-0" data-id="element-8087" data-mp="11ig3s3wnnbdu">
              <div className="text-xs font-bold text-emerald-100 uppercase tracking-wider mb-2 flex items-center gap-2" data-id="element-8088" data-mp="11h4trmcm97ea">
                <Users className="w-3.5 h-3.5" data-id="element-8089" data-mp="wm91qm7dqk16" />
                Wie heeft nu jouw aandacht?
              </div>
              <h1 className="text-2xl sm:text-3xl font-black text-white mb-2 leading-tight" data-id="element-8090" data-mp="16ic0di30w0jd">
                {childrenNeedingAttention === 0 ? `Alle ${linkedLearners.length} kinderen op koers` : childrenNeedingAttention === 1 ? '1 kind kan een zetje gebruiken' : `${childrenNeedingAttention} kinderen kunnen een zetje gebruiken`}
              </h1>
              <p className="text-sm text-emerald-50 max-w-2xl" data-id="element-8091" data-mp="nrb637cukt65">
                {childrenNeedingAttention === 0 ? 'Er zijn geen acute aandachtspunten — iedereen oefent regelmatig en kennis blijft fris.' : 'Kinderen met aandachtspunten staan bovenaan. Tik op een kaart om in te zoomen.'}
              </p>
            </div>
          </div>
        </Card>

        {/* Comparison grid — responsive: 1-col mobile, 2-col md+, 3-col xl when 3+ kids */}
        <div className={`grid gap-5 sm:gap-6 ${ranked.length >= 3 ? 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3' : 'grid-cols-1 md:grid-cols-2'}`} data-id="element-8092" data-mp="2bbm34ivraxb1">
          {ranked.map((s, i) => <motion.div key={s.learner.id} initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: i * 0.05,
          duration: 0.25
        }} data-id="element-8093" data-mp="21ehfsfq6cvsn" data-mpi={i}>
              <ChildCompareCard snapshot={s} onOpenDetail={() => {
            setViewingLearner(s.learner.id);
            onNavigate?.('home');
          }} onOpenSubjects={() => {
            setViewingLearner(s.learner.id);
            onNavigate?.('voortgang');
          }} data-id="element-8094" data-mp="1nkq2cw9borrn" />
            </motion.div>)}
        </div>

        {/* Footer note — explains the ranking so Mara isn't surprised */}
        <div className="text-xs text-slate-500 text-center max-w-2xl mx-auto pt-2" data-id="element-8095" data-mp="1dgrv7yzozhcn">
          Sortering: kinderen met wegzakkende kennis of langere inactiviteit
          staan bovenaan. Geen rangorde van prestaties.
        </div>
      </div>
    </PageLayout>;
}
interface ChildCompareCardProps {
  snapshot: ChildSnapshot;
  onOpenDetail: () => void;
  onOpenSubjects: () => void;
}
function ChildCompareCard({
  snapshot,
  onOpenDetail,
  onOpenSubjects
}: ChildCompareCardProps) {
  const {
    learner,
    overallMastery,
    currentStreak,
    daysThisWeek,
    lastActivityTs,
    staleDecayCount,
    fadingDecayCount,
    attentionReason
  } = snapshot;
  const initial = learner.displayName.charAt(0).toUpperCase();
  const hasData = overallMastery > 0 || lastActivityTs !== null;
  return <Card className="p-5 sm:p-6 bg-white border-slate-200 shadow-sm h-full flex flex-col" data-id="element-8096" data-mp="3v4vyorz3a6z9">
      {/* Header — child identity */}
      <div className="flex items-center gap-3 mb-4" data-id="element-8097" data-mp="8s74g7dkafvt">
        <div className="w-12 h-12 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-700 font-black text-lg flex-shrink-0" data-id="element-8098" data-mp="8tqlw1ayewz6">
          {initial}
        </div>
        <div className="min-w-0 flex-1" data-id="element-8099" data-mp="3cagyiz11k89u">
          <div className="text-base font-black text-slate-900 truncate" data-id="element-8100" data-mp="37a2z25dia6nf">
            {learner.displayName}
          </div>
          <div className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-8101" data-mp="3m7bsd4mddkhs">
            {learner.grade ? `Groep ${learner.grade}` : 'Groep —'}
          </div>
        </div>
      </div>

      {/* Attention banner — only when there's a reason */}
      {attentionReason && <div className="mb-4 p-3 rounded-xl bg-amber-50 border border-amber-100 flex items-start gap-2.5" data-id="element-8102" data-mp="wi1jmvlcsgj8">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-8103" data-mp="13zpyv0bgiyay" />
          <div className="min-w-0" data-id="element-8104" data-mp="19044ycthzy6c">
            <div className="text-xs font-bold text-amber-900 leading-tight" data-id="element-8105" data-mp="1915hklq5wsq6">
              Aandacht nodig
            </div>
            <div className="text-xs text-amber-800 leading-snug mt-0.5" data-id="element-8106" data-mp="v2fln9f2wz3e">
              {attentionReason}
            </div>
          </div>
        </div>}

      {/* On-track banner — affirmative for kids without attention flags */}
      {!attentionReason && hasData && <div className="mb-4 p-3 rounded-xl bg-emerald-50 border border-emerald-100 flex items-start gap-2.5" data-id="element-8107" data-mp="1rpk5v193hsna">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" data-id="element-8108" data-mp="1dwuk8xxhapqp" />
          <div className="text-xs font-bold text-emerald-900 leading-tight" data-id="element-8109" data-mp="1hypl61bhz13y">
            Op koers
          </div>
        </div>}

      {/* No-data state */}
      {!hasData && <div className="mb-4 p-3 rounded-xl bg-slate-50 border border-slate-100 flex items-start gap-2.5" data-id="element-8110" data-mp="2ahmpmlh19p1r">
          <Clock className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" data-id="element-8111" data-mp="mu2yqxfvo3xb" />
          <div className="text-xs font-medium text-slate-600 leading-snug" data-id="element-8112" data-mp="hsdmxfktzm86">
            Nog geen oefendata — laat {learner.displayName} eerst inloggen.
          </div>
        </div>}

      {/* Stat grid */}
      <div className="grid grid-cols-2 gap-3 mb-4" data-id="element-8113" data-mp="8u1y9j3iyyid">
        <CompareStat icon={<TrendingUp className="w-4 h-4" data-id="element-8115" data-mp="15hy8854givkm" />} label="Beheersing" value={hasData ? `${overallMastery}%` : '—'} tone="emerald" data-id="element-8114" data-mp="15iq65mlj15e2" />
        <CompareStat icon={<Flame className="w-4 h-4" data-id="element-8117" data-mp="vsenetx6orn4" />} label="Reeks" value={currentStreak > 0 ? `${currentStreak} ${currentStreak === 1 ? 'dag' : 'dagen'}` : '—'} tone="orange" data-id="element-8116" data-mp="vt6js6yluteg" />
        <CompareStat icon={<CalendarDays className="w-4 h-4" data-id="element-8119" data-mp="1jj0w8nf0ify6" />} label="Deze week" value={`${daysThisWeek}/7`} tone="blue" data-id="element-8118" data-mp="1oblldbmz44vd" />
        <CompareStat icon={<Clock className="w-4 h-4" data-id="element-8121" data-mp="327vdnkp9hfu6" />} label="Laatst" value={relativeTimeNL(lastActivityTs)} tone="slate" data-id="element-8120" data-mp="3273gi5g0na1s" />
      </div>

      {/* Decay chips — only when present */}
      {(staleDecayCount > 0 || fadingDecayCount > 0) && <div className="flex flex-wrap gap-1.5 mb-4" data-id="element-8122" data-mp="3g6355d3j3oxy">
          {staleDecayCount > 0 && <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-rose-50 text-rose-700 text-[11px] font-bold" data-id="element-8123" data-mp="3l3lhldgr8map">
              <AlertTriangle className="w-3 h-3" data-id="element-8124" data-mp="3q1d946dhm6ed" />
              {staleDecayCount} wegzakkend
            </span>}
          {fadingDecayCount > 0 && <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-amber-50 text-amber-700 text-[11px] font-bold" data-id="element-8125" data-mp="2pc9kujhhzjm">
              {fadingDecayCount} verzwakt
            </span>}
        </div>}

      {/* Actions — pushes to bottom of card */}
      <div className="mt-auto pt-2 flex gap-2" data-id="element-8126" data-mp="28r6ii1ukvy9d">
        <button onClick={onOpenDetail} className="flex-1 px-3 py-2.5 rounded-xl bg-slate-50 hover:bg-slate-100 text-slate-700 text-sm font-bold transition-colors flex items-center justify-center gap-1.5 border border-slate-200" data-id="element-8127" data-mp="28rzgoefh1ffw">
          Bekijk overzicht
          <ArrowRight className="w-3.5 h-3.5" data-id="element-8128" data-mp="2dq10x2omvksy" />
        </button>
        <button onClick={onOpenSubjects} className="px-3 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold transition-colors" title="Open vakken" data-id="element-8129" data-mp="2rho7pwg7cbpz">
          Vakken
        </button>
      </div>
    </Card>;
}
interface CompareStatProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  tone: 'emerald' | 'orange' | 'blue' | 'slate';
}
function CompareStat({
  icon,
  label,
  value,
  tone
}: CompareStatProps) {
  const tones: Record<CompareStatProps['tone'], string> = {
    emerald: 'bg-emerald-50 text-emerald-600',
    orange: 'bg-orange-50 text-orange-500',
    blue: 'bg-blue-50 text-blue-500',
    slate: 'bg-slate-50 text-slate-500'
  };
  return <div className="rounded-xl border border-slate-100 p-3 bg-white" data-id="element-8130" data-mp="3ioee2q0kgkos">
      <div className="flex items-center gap-2 mb-1" data-id="element-8131" data-mp="341cugh8zphx0">
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${tones[tone]}`} data-id="element-8132" data-mp="33yqnaiq0h4fb">
          {icon}
        </div>
        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-8133" data-mp="2uzlorceg9b35">
          {label}
        </div>
      </div>
      <div className="text-base font-black text-slate-900 truncate" title={value} data-id="element-8134" data-mp="2zzzq6bljpms7">
        {value}
      </div>
    </div>;
}